import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { registerClientTools, type ClientTool, type ToolContext, type ToolOutcome } from '@vitesse/base-ui/ai';
import {
  DatasetLoadError,
  fileFormat,
  LOAD_BUDGET_MS,
  MAX_DATASETS,
  MAX_ROWS,
  RESERVED_NAMES,
  resolveDatasets,
  TOTAL_ROW_BUDGET,
  type ConversationFiles,
  type DatasetRequest,
  type ResolveDeps,
} from '../../sandbox/datasets';
import type { Dataset } from '../../sandbox/protocol';
import { PREPARE_LIMIT_MS } from '../../sandbox/prepare';
import { GATEWAY_PARAM, resolveGateway, type GatewayScope } from './gateway-resolve';
import {
  GAP,
  LAST_EXPRESSION,
  PRINTED_BEFORE_STOP,
  PRINTED_FIRST,
  PRINTED_NOTHING,
  RAISED,
  RESET,
  RESTARTED,
  STDERR,
} from './sandbox-outcome';
import { registerSandboxCard } from './sandbox-card';

/*
 * RUNNING CODE, which is the only honest way to answer some questions.
 *
 * Every other data tool returns something a model can READ: two hundred points,
 * a page of symbols, a block of source. That ceiling is correct for them — a
 * model handed ten thousand rows is being asked to be a spreadsheet, and it is
 * bad at it — but it means the questions that need all ten thousand rows have no
 * answer at all. "Is this drift or noise", "which of these four signals leads the
 * other three", "what did the distribution look like before the changeover":
 * each is a scan, and none of them survives being sampled down to fit a prompt.
 *
 * So this tool moves the ANSWER rather than the data. The rows are fetched by the
 * host, handed to a Python interpreter that has no network, and what comes back
 * is what the code printed — a number, a table, a chart. The model reads the
 * result, never the rows.
 *
 * WHY IT IS NOT A `runSql` TOOL. `docs/tool-design.md` measures the alternative:
 * on data of this shape, text-to-SQL scores about ten percent, because the hard
 * part is schema discovery and joins across unfamiliar tables rather than
 * expressing the query. Here the datasets are named and resolved BEFORE any code
 * runs, so there is no schema to discover and nothing to join wrongly — and the
 * analysis happens in a place where a mistake is visible, re-runnable and costs
 * a retry rather than a wrong number delivered confidently.
 */

export interface SandboxToolDeps extends GatewayScope, ResolveDeps {
  /**
   * Has this ORGANISATION switched code execution on?
   *
   * Read per call rather than captured at registration, because the answer arrives with the
   * handshake and can change under a live tab — a tool registered before the session resolved
   * would otherwise be stuck with whatever was true at boot, which is `false`, forever.
   */
  codeExecutionEnabled: () => boolean;
  /** The files of one conversation, for `file` datasets. A run reads only its own conversation's. */
  conversationFiles?: (conversationId: string) => ConversationFiles;
}

/**
 * What the sandbox can import, as the model is told it — kept beside the tool rather than in the
 * prompt so there is one list, and test/sandbox-catalog.test.ts checks it against what ships
 * (tools/wheels-lib.mjs, tools/pypi-wheels.json). micropip ships but is not offered: with no
 * network there is nothing for it to install.
 */
export const CATALOG =
  'numpy, pandas, scipy, statsmodels, scikit-learn (import sklearn), matplotlib, duckdb, xlsxwriter, openpyxl and tabulate';

/** Long enough for a real scan over a million rows; short enough that a runaway
 *  loop is a pause rather than a hung afternoon. The worker is killed at it. */
const DEFAULT_TIMEOUT_S = 30;
const MAX_TIMEOUT_S = 120;

export function registerSandboxTools(deps: SandboxToolDeps): () => void {
  /** False once the tool is torn down (a sign-out), so a warm still on its way does not boot an
   *  interpreter after `releaseAll` has cleared them. */
  let live = true;
  const run: ClientTool = {
    name: 'sandbox.run',
    description:
      'Run Python over data from this platform, in a sandbox with no network. Use it when the answer needs MORE rows than you ' +
      'can read: statistics over a long window, correlating several signals, finding when a distribution changed, fitting a ' +
      'trend, or drawing a chart. Name the data you want in `datasets` and it arrives as a pandas DataFrame under that name — ' +
      'do NOT try to fetch data from inside the code, there is no network. Import what you use and it is installed before the ' +
      `code runs, outside timeoutSeconds: ${CATALOG}, beside the standard library — nothing else, and no pip. ` +
      'Print what you want to know; stdout comes back to you. Any file the code writes to its working directory — ' +
      'a chart, a CSV, a workbook — is handed to the person as an attachment. For a workbook, call vitesse.excel(…), which is ' +
      'already loaded — no import — rather than to_excel: it styles the sheets, writes times in UTC and adds a Sources sheet ' +
      'saying where each column came from. help(vitesse.excel) lists its options. State comes with you between calls in a ' +
      'conversation, so you can build a frame in one call and plot it in the next.',
    mutates: false,
    /*
     * ITS OWN PERMISSION, not the data's.
     *
     * Gating this on `history:read` — the permission of the rows it loads — was
     * the first shape and it is nearly right: rows the caller cannot read are
     * rows the sandbox cannot obtain, because every dataset resolves through the
     * same backend call the equivalent read tool makes, on this session. So the
     * DATA is already covered without a new key.
     *
     * What that misses is the question an administrator is actually asking. "May
     * this agent read our process history" and "may this agent execute code we
     * did not write" are separate decisions, and an org saying yes to the first
     * has not said yes to the second. It is also the key the long-dormant
     * `code_execution_enabled` toggle finally has something to stand on.
     */
    permission: Permission.AI_SANDBOX_RUN,
    /*
     * AND THE ORG'S CAPABILITY, which is the other half of the same question.
     *
     * `code_execution_enabled` has existed as a column, a config field, a console
     * checkbox and a handshake field since before this tool did, and every one of
     * those references was plumbing: it was read, stored, defaulted and reported,
     * and gated nothing. An org that switched it off ran code exactly as before.
     * That is the worst kind of switch — one a person believes.
     *
     * ENFORCED HERE BECAUSE HERE IS THE DOOR. The sandbox runs in this browser, so
     * there is no later server hop that could refuse it; unlike web search, whose
     * door is an RPC, the executor and the gate are the same process. That is worth
     * stating plainly rather than implying more: this is a CAPABILITY policy, not a
     * data boundary. The rows the code reads are fetched through the same
     * permission-checked calls the equivalent read tool makes, under the person's own sign-in, so
     * an org with the toggle off is not the thing standing between a user and data
     * they may not see — their grants are. This decides whether we run code we did
     * not write, and it is the org's decision to make.
     */
    unavailable: () =>
      deps.codeExecutionEnabled()
        ? null
        : 'Code execution is turned off for this organization, so there is no sandbox to run in. An administrator can enable it in the AI configuration. Answer from the read tools instead — history.query returns a bounded series you can reason about directly.',
    parameters: {
      type: 'object',
      properties: {
        /*
         * FOR THE PERSON, NOT THE SANDBOX: the title of this run's card in the transcript. The code
         * never sees it. Declared first because arguments tend to arrive in the order they are
         * declared, so the title is there before the code starts streaming in under it. Optional —
         * without it the card uses the code's first comment line.
         */
        label: {
          type: 'string',
          description:
            'A few words saying what this run computes, e.g. "10-minute average flow per pump". Shown to the person as its title.',
        },
        code: {
          type: 'string',
          description:
            'Python. The datasets you named are already bound as DataFrames. print() what you want back; the value of the last ' +
            'expression is returned too. matplotlib savefig("name.png") returns an image.',
        },
        datasets: {
          type: 'array',
          description:
            'The data to load before the code runs. Each becomes a pandas DataFrame named `name`. Leave empty for pure computation.',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'The Python variable name to bind it to. Letters, digits, underscores.' },
              source: {
                type: 'string',
                enum: ['history', 'model', 'file', 'integration', 'inline'],
                description:
                  '`history` for recorded samples, `model` for what a state machine recorded, `file` for a CSV or Excel file in ' +
                  "this conversation, `integration` for a table inside one of this gateway's external systems, `inline` for rows " +
                  'you supply (which is also how you bring in the result of any other tool, including MCP).',
              },
              gateway: GATEWAY_PARAM,
              controller: { type: 'string', description: 'history: controller id or name.' },
              symbolPath: { type: 'string', description: 'history: the symbol path, exactly as symbols.search returned it.' },
              minutesAgo: { type: 'number', description: 'history and model: how far back. Defaults to 60.' },
              bucketSeconds: {
                type: 'number',
                description:
                  "history and a model's output: seconds per bucket for a min/max/avg envelope (columns ts, avg, min, max, samples). Omit for raw " +
                  'samples (columns ts, value, quality). Raw is fine here — unlike history.query this is not capped at 200 points.',
              },
              model: { type: 'string', description: 'model: which one, by id or name — from models.list.' },
              output: {
                type: 'string',
                description: 'model: one of its declared outputs, from models.list (columns ts, value, quality). Only those are recorded.',
              },
              transitions: {
                type: 'boolean',
                description:
                  'model: true for its state changes instead of an output — columns ts, from, to (every state active after the ' +
                  'change, parents included) and event.',
              },
              integration: { type: 'string', description: 'integration: which one, by id or name — from integrations.list.' },
              target: {
                type: 'string',
                description:
                  'integration: the table/collection/path to load, exactly as integrations.browse returned it. The WHOLE ' +
                  'target is loaded, not the fifty rows integrations.read shows you — that is the point of loading it here.',
              },
              maxRows: { type: 'number', description: `integration: stop after this many rows. Defaults to the cap (${MAX_ROWS}).` },
              file: {
                type: 'string',
                description: 'file: its name as attached, e.g. "mes-export.xlsx". Read by pandas; it is also at /data/<name>.',
              },
              sheet: { type: 'string', description: 'file: the Excel sheet to read. Defaults to the first.' },
              rows: { type: 'array', description: 'inline: the rows, as objects.', items: { type: 'object' } },
            },
            required: ['name', 'source'],
          },
        },
        /*
         * NO `packages`. The code's imports say what it needs and the sandbox installs exactly that
         * (prepare.ts), so a run can no longer fail for naming the wrong tier. A call that still
         * passes `packages` — an older conversation, a habit — is not refused: it is ignored.
         */
        timeoutSeconds: { type: 'number', description: `Wall-clock limit. Defaults to ${DEFAULT_TIMEOUT_S}, max ${MAX_TIMEOUT_S}.` },
      },
      required: ['code'],
      additionalProperties: false,
    },

    /*
     * WARMED WHILE THE MODEL THINKS (`ClientTool.prewarm`): boot this conversation's interpreter
     * and import pandas when a run whose specialist holds this tool starts, so the first call does
     * not pay for them. Through a dynamic import, like the run itself — warming must not pull the
     * runtime into the critical bundle. Everything else — a full pool, a repeat, a failure — is
     * `warm()`'s to decide.
     */
    prewarm({ conversationId }) {
      void import('../../sandbox/pool')
        .then((m) => {
          if (live) m.warm(conversationId);
        })
        .catch(() => undefined);
    },

    async run(args, ctx?: ToolContext): Promise<ToolOutcome> {
      const code = String(args.code ?? '').trim();
      if (!code) return { ok: false, message: 'There was no code to run — pass the Python in `code`.' };

      const requested = Array.isArray(args.datasets) ? (args.datasets as Record<string, unknown>[]) : [];
      /*
       * THE LIMIT IS ROWS, NOT DATASETS.
       *
       * Eight datasets was the old ceiling, and on a bench it cost a twenty-signal question seven of
       * the eight tool rounds a chat turn gets, loading in batches before any analysis ran. What a
       * run can afford is decided by `TOTAL_ROW_BUDGET` in `resolveDatasets`; this count is only a
       * guard against a model that loops.
       */
      if (requested.length > MAX_DATASETS) {
        return {
          ok: false,
          message: `That asks for ${requested.length} datasets; one run takes at most ${MAX_DATASETS}. Load what this question needs; you can run again for the rest.`,
        };
      }

      // EVERY REQUEST IS CHECKED BEFORE ANY IS FETCHED, so a typo in the twentieth dataset does not
      // cost nineteen round trips to the historian first.
      const valid: DatasetRequest[] = [];
      for (const raw of requested) {
        const req = toRequest(deps, raw);
        if (!req.ok) return { ok: false, message: req.message };
        valid.push(req.value);
      }
      /*
       * ONE NAME, ONE DATASET. Two datasets under one name would both load and the second would
       * silently replace the first in Python, so the code would analyse a series the model believes
       * it is not looking at.
       */
      const seen = new Set<string>();
      for (const r of valid) {
        if (seen.has(r.name)) {
          return {
            ok: false,
            message: `Two datasets are named "${r.name}". Give each one its own name — it becomes the variable in Python.`,
          };
        }
        seen.add(r.name);
      }
      const relayOf = new Map(valid.map((r) => [r.name, r.source === 'inline' || r.source === 'file' ? '' : r.gatewayId]));

      const timeoutMs = Math.min(MAX_TIMEOUT_S, Math.max(1, Number(args.timeoutSeconds) || DEFAULT_TIMEOUT_S)) * 1000;
      /*
       * KEYED BY THE CALLING THREAD, which `ctx` is here to answer.
       *
       * A sub-agent has its own conversation, so a fan-out of specialists each
       * get their own interpreter without anyone arranging it: they cannot see
       * each other's variables, and one crunching a million rows does not make
       * the others queue behind it. That is what makes the parallelism real
       * rather than a queue with extra steps.
       */
      /*
       * THE RUNTIME LOADS ON FIRST USE, never at boot.
       *
       * The declaration above has to be registered eagerly — the model can only
       * be offered a tool that is in the catalogue when the turn starts — but the
       * megabyte of Pyodide loader behind it must not be in the critical bundle
       * of an app that mostly never runs Python.
       */
      const { sessionFor, takeRestartNotice } = await import('../../sandbox/pool');
      const conversationId = ctx?.conversationId ?? '';
      const session = sessionFor(conversationId);
      /*
       * THE INTERPRETER COMES UP WHILE THE DATA LOADS.
       *
       * Neither needs the other, and they used to run one after the other: the
       * historian reads, then the boot. Started here and awaited below, a cold run
       * waits for the longer of the two instead of their sum. Settled at once into
       * a value, so a start that fails while the data is still loading is held for
       * the check below rather than going unhandled.
       */
      const starting = session.start().then(
        () => null,
        (e: unknown) => e,
      );

      // RESOLVED BEFORE THE RUN, and a failure here never reaches the interpreter:
      // a run whose data did not load would report a NameError, which sends the
      // model hunting through its own code for a fault that is not there. The
      // interpreter that started meanwhile is kept for the next run.
      let datasets;
      try {
        datasets = await resolveDatasets({ ...deps, files: conversationId ? deps.conversationFiles?.(conversationId) : undefined }, valid);
      } catch (e) {
        const which = e instanceof DatasetLoadError ? e.dataset : '';
        const why = e instanceof Error ? e.message : String(e);
        return { ok: false, message: which ? `Could not load "${which}": ${why}` : `Could not load the datasets: ${why}` };
      }

      const waitFrom = performance.now();
      const failedToStart = await starting;
      if (failedToStart) {
        return {
          ok: false,
          message: `The sandbox could not start: ${failedToStart instanceof Error ? failedToStart.message : String(failedToStart)}`,
          retryable: true,
        };
      }
      const startWait = performance.now() - waitFrom;
      const restarted = takeRestartNotice(conversationId);

      let result;
      try {
        result = await session.run(code, datasets, timeoutMs);
      } catch (e) {
        // The session was shut down under the run — a sign-out, a wedged preload. A fresh one is
        // handed out on the next call, so it is worth repeating.
        return {
          ok: false,
          message: `The sandbox stopped before the run finished: ${e instanceof Error ? e.message : String(e)}.`,
          retryable: true,
        };
      }

      /*
       * PLAIN NUMBERS, not grouped ones.
       *
       * This sentence is read by a MODEL, and the host-locale rule points the same
       * way for a second reason here: `1 234 567` in a French browser and `1,234,567`
       * in an American one are two different strings for one fact, and a model asked
       * to reason about "1,234" may read a thousand or may read one point two three
       * four. A row count going to a machine is a number, not a presentation.
       */
      // A file's rows are known only once pandas has read it, in the sandbox.
      for (const d of datasets) {
        if (d.file && result.rows?.[d.name] !== undefined) d.rows = result.rows[d.name]!;
        if (d.file && result.sheets?.[d.name]) d.sheets = result.sheets[d.name];
      }
      const head = `${restarted ? `${RESTARTED}${GAP}` : ''}${datasets.length ? `${describeDatasets(datasets)}${GAP}` : ''}`;

      if (result.exitCode === 124) {
        // Which clock ran out matters to what the model does next: a long install is the sandbox's,
        // and asking for more timeoutSeconds would not have helped it.
        const what =
          result.stoppedIn === 'prepare'
            ? `Getting ready — installing the packages the code imports and loading the datasets — took longer than ${Math.round(PREPARE_LIMIT_MS / 1000)}s, so the run was stopped before the code started`
            : `The code was still running after ${timeoutMs / 1000}s and was stopped`;
        return {
          ok: false,
          message:
            `${head}${what}, ${RESET} ` +
            `Anything it had in memory is gone.${result.stdout ? `${PRINTED_BEFORE_STOP}${result.stdout}` : ''}`,
        };
      }
      if (result.exitCode !== 0) {
        // The traceback IS the useful part: the model reads it and fixes the next
        // call. Returned as a failure so it retries rather than reporting a result.
        return { ok: false, message: `${head}${RAISED}${result.stderr}${result.stdout ? `${PRINTED_FIRST}${result.stdout}` : ''}` };
      }

      const parts: string[] = [];
      if (result.stdout.trim()) parts.push(result.stdout.trimEnd());
      if (result.result !== null && result.result !== 'None') parts.push(`${LAST_EXPRESSION}${result.result}`);
      if (result.stderr.trim()) parts.push(`${STDERR}${result.stderr.trimEnd()}`);
      for (const f of result.files) parts.push(`produced ${f.filename} (${f.mediaType}, ${f.bytes.byteLength} bytes)`);
      if (!parts.length) parts.push(PRINTED_NOTHING);

      return {
        ok: true,
        message: `${head}${parts.join(GAP)}`,
        data: {
          exitCode: 0,
          ms: Math.round(result.ms),
          // Installing and binding, timed apart from the code. Like everything in `data`, the model
          // receives it after the message (see sandbox-outcome.ts) — a few numbers, not prose.
          ...(result.prepareMs !== undefined ? { prepareMs: Math.round(result.prepareMs) } : {}),
          // And before that, the wait for the interpreter itself beyond the data load: its boot, a
          // preload still in flight, an earlier run. What warming exists to bring to zero, and the
          // number that says whether it did.
          startMs: Math.round(startWait + (result.waitedMs ?? 0)),
          // loadMs and window ride in `data` rather than the message: what a slow run spent its time on,
          // per dataset, kept for the transcript card and the archive. The model receives `data` as JSON
          // after the message, so it is a few fields, never a second copy of what the message says.
          datasets: datasets.map((d) => ({
            name: d.name,
            rows: d.rows,
            source: d.source,
            truncated: !!d.truncated,
            ...(d.truncatedBy ? { truncatedBy: d.truncatedBy } : {}),
            ...(d.asked ? { asked: d.asked } : {}),
            ...(d.samples ? { samples: d.samples } : {}),
            ...(d.window ? { window: d.window } : {}),
            ...(d.loadMs !== undefined ? { loadMs: d.loadMs } : {}),
            // The relay it was read from: a controller id is only unique on its relay (every relay has
            // a "Core"), and a finding that copies this run as evidence says which machine it is about.
            ...(relayOf.get(d.name) ? { gatewayId: relayOf.get(d.name) } : {}),
          })),
          files: result.files.map((f) => ({ filename: f.filename, mediaType: f.mediaType, size: f.bytes.byteLength })),
        },
        /*
         * THE CHART ITSELF, not a sentence about it.
         *
         * The message above says "produced chart.png (image/png, 41,203 bytes)" and that is the
         * right thing to tell a MODEL — it cannot look at a PNG, and putting one in its context
         * would cost a hundred kilobytes of prompt every turn thereafter for something it cannot
         * read. But a description of a chart nobody can open is most of the value of having drawn
         * it thrown away, and drawing charts is a third of what this tool is for.
         *
         * So the bytes take the archive path: parked in the conversation's blob store and written
         * as a `file` part beside the result, which is what the transcript renders and what a
         * person clicks. The model's turn is untouched.
         */
        ...(result.files.length
          ? { attachments: result.files.map((f) => ({ filename: f.filename, mediaType: f.mediaType, bytes: new Uint8Array(f.bytes) })) }
          : {}),
      };
    },
  };

  const off = registerClientTools(run);
  // And how a run LOOKS in the transcript — registered beside the tool, because they are one feature.
  const offCard = registerSandboxCard();
  return () => {
    live = false;
    off();
    offCard();
    /*
     * AND EVERY WARM INTERPRETER GOES WITH THE TOOL.
     *
     * A session holds an account's rows in a live worker. When the shell that
     * authorised it tears down — a sign-out, a surface change — leaving those
     * warm would mean the next account on this tab shares a browser with the
     * previous one's data. Torn down HERE rather than in the shell so the
     * runtime stays out of the critical bundle: the import only resolves if the
     * sandbox was ever used, and if it was not there is nothing to release.
     */
    void import('../../sandbox/pool').then((m) => m.releaseAll()).catch(() => undefined);
  };
}

type Parsed = { ok: true; value: DatasetRequest } | { ok: false; message: string };

/** Turn what the model wrote into a resolvable request, or say what is missing.
 *  Validated here rather than in the resolver so the message can name the tool's
 *  own argument shape, which is what the model is looking at. */
function toRequest(deps: SandboxToolDeps, raw: Record<string, unknown>): Parsed {
  const name = String(raw.name ?? '').trim();
  if (!name) return { ok: false, message: 'Every dataset needs a `name` — it becomes the DataFrame variable.' };
  // Refused before anything is fetched, like every other request error here.
  if (RESERVED_NAMES.has(name))
    return { ok: false, message: `"${name}" is reserved for the sandbox's own helpers — give the dataset another name.` };
  const source = String(raw.source ?? 'history');

  if (source === 'inline') {
    const rows = Array.isArray(raw.rows) ? (raw.rows as Record<string, unknown>[]) : null;
    if (!rows) return { ok: false, message: `Dataset "${name}" is inline but has no \`rows\`.` };
    return { ok: true, value: { source: 'inline', name, rows } };
  }

  if (source === 'integration') {
    const gwi = resolveGateway(deps, raw.gateway);
    if (!gwi.ok) return { ok: false, message: gwi.message };
    const asked = String(raw.integration ?? '').trim();
    if (!asked) return { ok: false, message: `Dataset "${name}" needs an \`integration\` — \`integrations.list\` names them.` };
    const wantedI = asked.toLowerCase();
    const intg = gwi.value.entry.integrations.all.find((i) => i.id.toLowerCase() === wantedI || i.displayName.toLowerCase() === wantedI);
    if (!intg) {
      const known = gwi.value.entry.integrations.all.map((i) => i.displayName || i.id);
      return {
        ok: false,
        message: known.length
          ? `No integration "${asked}" on this relay. It has: ${known.join(', ')}.`
          : 'This relay has no integrations configured.',
      };
    }
    const target = String(raw.target ?? '').trim();
    if (!target) return { ok: false, message: `Dataset "${name}" needs a \`target\` — \`integrations.browse\` returns the exact path.` };
    const maxRows = Number(raw.maxRows) > 0 ? Math.floor(Number(raw.maxRows)) : undefined;
    return {
      ok: true,
      value: { source: 'integration', name, gatewayId: gwi.value.id, integrationId: intg.id, target, ...(maxRows ? { maxRows } : {}) },
    };
  }

  if (source === 'model') return modelRequest(deps, raw, name);

  if (source === 'file') {
    const file = String(raw.file ?? '').trim();
    if (!file) return { ok: false, message: `Dataset "${name}" needs a \`file\` — the file's name as it is attached.` };
    const format = fileFormat(file);
    if (!format)
      return { ok: false, message: `Dataset "${name}": "${file}" is not a CSV or an Excel (.xlsx) file, the two a file dataset reads.` };
    const sheet = String(raw.sheet ?? '').trim();
    return { ok: true, value: { source: 'file', name, file, format, ...(sheet && format === 'xlsx' ? { sheet } : {}) } };
  }

  if (source !== 'history') {
    return {
      ok: false,
      message: `Dataset "${name}" names an unknown source "${source}". Use "history", "model", "file", "integration" or "inline".`,
    };
  }

  const gw = resolveGateway(deps, raw.gateway);
  if (!gw.ok) return { ok: false, message: gw.message };
  const controllerRef = String(raw.controller ?? '').trim();
  if (!controllerRef) return { ok: false, message: `Dataset "${name}" needs a \`controller\`.` };
  const symbolPath = String(raw.symbolPath ?? '').trim();
  if (!symbolPath) return { ok: false, message: `Dataset "${name}" needs a \`symbolPath\` — \`symbols.search\` returns the exact path.` };

  const wanted = controllerRef.toLowerCase();
  const controller = gw.value.entry.controllers.all.find((c) => c.id.toLowerCase() === wanted || c.name.toLowerCase() === wanted);
  if (!controller) {
    const known = gw.value.entry.controllers.all.map((c) => `${c.name} (${c.id})`);
    return {
      ok: false,
      message: `No controller "${controllerRef}" on this relay.${known.length ? ` The controllers here are: ${known.join(', ')}.` : ''}`,
    };
  }

  return {
    ok: true,
    value: {
      source: 'history',
      name,
      gatewayId: gw.value.id,
      controllerId: controller.id,
      symbolPath,
      ...windowOf(raw),
      bucketSeconds: Number(raw.bucketSeconds) > 0 ? Number(raw.bucketSeconds) : null,
    },
  };
}

/** The window a history-shaped dataset reads: `minutesAgo` back from now, an hour by default. */
function windowOf(raw: Record<string, unknown>): { fromMs: number; toMs: number } {
  const minutes = Number(raw.minutesAgo) > 0 ? Number(raw.minutesAgo) : 60;
  const toMs = Date.now();
  return { fromMs: toMs - minutes * 60_000, toMs };
}

/**
 * A MODEL'S RECORDED DATA: one of its declared outputs, or its state changes.
 *
 * Both are history under the model's own controller id, `model:<mid>`, so this is a history read
 * with the model looked up instead of a controller — the same reader, the same session.
 *
 * ONLY A DECLARED OUTPUT IS RECORDED, and only when its value changes (the runner's rule, not the
 * descriptors'). Any other name would read a series that is always empty, so it is refused here,
 * naming the outputs that are recorded.
 */
function modelRequest(deps: SandboxToolDeps, raw: Record<string, unknown>, name: string): Parsed {
  const gw = resolveGateway(deps, raw.gateway);
  if (!gw.ok) return { ok: false, message: gw.message };
  const ref = String(raw.model ?? '').trim();
  if (!ref) return { ok: false, message: `Dataset "${name}" needs a \`model\` — \`models.list\` names them.` };
  const wanted = ref.toLowerCase();
  const models = gw.value.entry.models.all;
  const model = models.find((m) => m.id.toLowerCase() === wanted || m.name.toLowerCase() === wanted);
  if (!model) {
    const known = models.map((m) => `${m.name} (${m.id})`);
    return {
      ok: false,
      message: `No model "${ref}" on this relay.${known.length ? ` The models here are: ${known.join(', ')}.` : ' It has no models.'}`,
    };
  }
  const base = { source: 'history' as const, name, gatewayId: gw.value.id, controllerId: `model:${model.id}`, ...windowOf(raw) };

  if (raw.transitions === true) {
    if (Number(raw.bucketSeconds) > 0) {
      return { ok: false, message: `Dataset "${name}": state changes are read one row per change; leave out \`bucketSeconds\`.` };
    }
    return { ok: true, value: { ...base, symbolPath: '$events.state_transition', bucketSeconds: null, transitions: true } };
  }

  const outputs = model.outputs ?? [];
  const output = String(raw.output ?? '').trim();
  if (!output || !outputs.includes(output)) {
    const recorded = outputs.length
      ? `Its recorded outputs are: ${outputs.join(', ')}.`
      : 'It declares no outputs, so only its state changes are recorded — set `transitions` to true.';
    return {
      ok: false,
      message: `Dataset "${name}": ${output ? `"${output}" is not a declared output of "${model.name}".` : `name an \`output\` of "${model.name}", or set \`transitions\`.`} Only declared outputs are recorded. ${recorded}`,
    };
  }
  return {
    ok: true,
    value: { ...base, symbolPath: output, bucketSeconds: Number(raw.bucketSeconds) > 0 ? Number(raw.bucketSeconds) : null },
  };
}

/**
 * What the model is told about the datasets a run loaded: one line each, before anything the code
 * printed.
 *
 * THE SAMPLES' SPAN BESIDE THE WINDOW. A count alone hid the commonest way data goes wrong: on a
 * bench the model read "43 rows" for a window whose first half held nothing, and reported the window
 * as covered, twice. Where the first and last samples fall says that without the model having to
 * think to look, and a long stretch with nothing at the START is named outright: what the value was
 * before its first sample is not in the data. The END is not named. The last value holds until the
 * window closes — the prompt says to weight it so — and naming a quiet end as "none" read, on the
 * bench, as four minutes of unknown state that the model's own code had correctly counted.
 *
 * WHY A DATASET WAS CUT, because the remedy differs: a row share asks for fewer or coarser datasets,
 * the load budget for a shorter window, and the part it holds is named either way.
 *
 * PLAIN NUMBERS, not grouped ones: this is read by a model, and `1 234 567` in a French browser and
 * `1,234,567` in an American one are two strings for one fact. Times are UTC to the second.
 *
 * A FILE'S TIMES ARE NOT OURS TO ZONE. They are what whoever wrote it wrote, most often plant local
 * time with no zone at all. A header saying "times UTC" over a file only was believed on the bench:
 * the model reported an MES export's local times as UTC, against its own doubt. So the header says
 * UTC only over data we timed, and a file's line says its times are as written.
 */
export function describeDatasets(datasets: readonly Dataset[]): string {
  const asked = datasets.map((d) => d.asked).filter((w): w is NonNullable<Dataset['asked']> => !!w);
  // Every history dataset in a run is asked for the same minutes back, a few ms apart.
  const shared =
    asked.length === datasets.length && asked.every((w) => Math.abs(w.fromMs - asked[0]!.fromMs) < 1_000 && Math.abs(w.toMs - asked[0]!.toMs) < 1_000)
      ? asked[0]!
      : null;
  const ref = shared ?? asked[0];
  // Within one UTC day the date is said once, in the header; otherwise every time carries it.
  const oneDay = !!ref && datasets.every((d) => !d.asked || (day(d.asked.fromMs) === day(ref.fromMs) && day(d.asked.toMs) === day(ref.fromMs)));
  const at = (ms: number): string => (oneDay ? new Date(ms).toISOString().slice(11, 19) + 'Z' : utc(ms));

  const n = datasets.length;
  const share = Math.min(MAX_ROWS, Math.floor(TOTAL_ROW_BUDGET / Math.max(1, n)));
  const lines = datasets.map((d) => {
    const bits: string[] = [];
    const kind = d.source.match(/· (\d+)s buckets$/);
    // A file's rows are counted when pandas reads it; a run that stopped before then has no count.
    bits.push(kind ? `${d.rows} buckets of ${kind[1]} s` : d.rows < 0 ? 'rows not counted' : `${d.rows} rows`);
    if (!d.asked) bits.push(`from ${d.source}`);
    else if (!shared) bits.push(`window ${at(d.asked.fromMs)} → ${at(d.asked.toMs)}`);
    if (d.file) bits.push('times as the file writes them, in no stated zone');
    if (d.file && d.sheets) {
      // A sheet asked for is already in the source; the first, read by default, is named here.
      const read = d.file.sheet ?? d.sheets[0]!;
      const others = d.sheets.filter((s) => s !== read).map((s) => `"${s}"`);
      bits.push(`${d.file.sheet ? '' : `the first sheet, "${read}"; `}the workbook also has ${others.join(', ')} (name one with sheet)`);
    }
    // A dataset the load budget never reached is not an empty window, and must not read like one: on a
    // bench, four such datasets were reported as "recorded nothing in the window".
    const unread = d.truncated && d.truncatedBy === 'time' && d.rows === 0;
    if (unread) bits.push(`NOT READ: the run's ${LOAD_BUDGET_MS / 1000} s load budget ran out before its turn`);
    else if (d.asked && d.rows === 0) bits.push('nothing was recorded in the window');
    else if (d.asked && d.samples) {
      let span = `samples ${at(d.samples.firstMs)} → ${at(d.samples.lastMs)}`;
      const quiet = gapWorthNaming(d.asked);
      // Measured against the part it holds: a cut dataset's missing part is the cut, said below.
      const held = d.truncated && d.window ? d.window : d.asked;
      const lead = d.samples.firstMs - held.fromMs;
      if (lead > quiet) span += `, none in the first ${duration(lead)}`;
      bits.push(span);
    }
    if (d.truncated && !unread) {
      const holds = d.window ? `: it holds only ${at(d.window.fromMs)} → ${at(d.window.toMs)}` : '';
      const why =
        d.truncatedBy === 'time'
          ? `TRUNCATED when the run's ${LOAD_BUDGET_MS / 1000} s load budget ran out`
          : d.truncatedBy === 'historian'
            ? 'TRUNCATED because the historian stopped returning further pages'
            : `TRUNCATED at its row share (${share} rows per dataset when a run loads ${n})`;
      bits.push(why + holds);
    }
    for (const note of d.notes ?? []) bits.push(`the historian said: ${note}`);
    return `- ${d.name}: ${bits.join('; ')}`;
  });

  const header = shared
    ? `Loaded ${n} dataset${n === 1 ? '' : 's'} over ${utc(shared.fromMs)} → ${oneDay ? at(shared.toMs) : utc(shared.toMs)} (UTC):`
    : `Loaded ${n} dataset${n === 1 ? '' : 's'}${datasets.some((d) => !d.file) ? ' (times UTC)' : ''}:`;
  const advice = datasets.some((d) => d.truncated)
    ? '\nA TRUNCATED or NOT READ dataset covers only part of what was asked, or none of it: say so in any conclusion, or ask again with a bucketSeconds, a shorter window or fewer datasets.'
    : '';
  return `${header}\n${lines.join('\n')}${advice}`;
}

const utc = (ms: number): string => new Date(ms).toISOString().slice(0, 19) + 'Z';
const day = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** A quiet stretch is named when it is a tenth of the window and at least a minute — shorter ones are
 *  how a value that changes on its own schedule looks. */
const gapWorthNaming = (w: { fromMs: number; toMs: number }): number => Math.max(60_000, (w.toMs - w.fromMs) / 10);

function duration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 120) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 120) return `${m} min`;
  return `${Math.round((m / 60) * 10) / 10} h`;
}
